import type { PostPlatformSettingsMap, SettingsPlatform } from './postPlatformSettings';
import { isYouTubePrivacyStatus, type YouTubePrivacyStatus } from './youtubePostSettings';

/**
 * "Schedule all N posts" of a campaign in one step (owner report 2026-10-10: "I just want to
 * launch my campaign").
 *
 * `POST /accounts/:accountId/promo/posts/launch { postUuids, answers?, dryRun? }` schedules every
 * draft that can go out and names each draft that cannot, with the reason. It never refuses the
 * whole request because of one post.
 *
 * Which settings does a post need, and who decides them:
 *
 * | Platform | Field | Rule | What the launch does |
 * |---|---|---|---|
 * | YouTube | privacy status | our choice to ask (the API takes any status) | asked one time for each campaign; the stored default of the channel is selected, else "Public"; the answer is the new default |
 * | YouTube | made for kids | YouTube rule: the uploader declares it | asked one time for EACH campaign with no selection; never stored, never taken from an earlier answer (owner decision 2026-10-11) |
 * | TikTok | privacy level | TikTok rule: the user picks it, NO selected value | asked one time for each campaign, never stored |
 * | TikTok | comments, Duet, Stitch | TikTok rule: off until the user turns them on | off; the question shows the switches |
 * | TikTok | commercial content | TikTok rule: off until the user turns it on | off; the question shows the switch |
 * | Instagram, Facebook, Threads | none | | no question |
 *
 * The web app, the server and the tests read this file. No imports of other packages.
 */

/**
 * The most drafts of one launch. An AI reels plan of 30 reels on five channels has 150 drafts; a
 * release rollout on five channels has fewer.
 */
export const CAMPAIGN_LAUNCH_MAX_POSTS = 200;

/**
 * The privacy status that the YouTube question shows as selected when the channel holds no stored
 * default. The user can change it.
 */
export const YOUTUBE_SUGGESTED_PRIVACY: YouTubePrivacyStatus = 'public';

/** The answers of the user for the whole campaign: one entry for each platform that asked. */
export type CampaignLaunchAnswers = PostPlatformSettingsMap;

/**
 * Why a draft was not scheduled. Each reason has one fix in the app.
 * - `needsAnswer`: its platform needs an answer and the request gave none (Answer).
 * - `reconnect`: its channel is signed out (Reconnect).
 * - `mediaMissing`: it has no file, or its file is gone (Open the post).
 * - `mediaRefused`: the platform does not take its file; `detail` says why (Open the post).
 * - `monthLimit`: the plan has no scheduled posts left this month (See plans).
 * - `channelLimit`: the channel is above the plan (See plans).
 * - `timePast`: its time is in the past (Open the post).
 * - `tiktokWatermark`: TikTok takes no file with the Music24 mark of the Free plan (See plans).
 * - `xMonthLimit`: its X account has no API posts left this month (`xPostsPerMonth`); the post
 *   stays a draft (Post it by hand, or give it a time next month).
 */
export type CampaignLaunchBlockReason =
  | 'needsAnswer'
  | 'reconnect'
  | 'mediaMissing'
  | 'mediaRefused'
  | 'monthLimit'
  | 'channelLimit'
  | 'timePast'
  | 'tiktokWatermark'
  | 'xMonthLimit';

export interface CampaignLaunchBlockedPost {
  postUuid: string;
  platform: string;
  reason: CampaignLaunchBlockReason;
  /** One English sentence of the server for `mediaRefused`, else null. */
  detail: string | null;
}

/** One question of the dialog: a platform whose drafts need the answer of the user. */
export interface CampaignLaunchQuestion {
  platform: SettingsPlatform;
  /** The drafts that wait for this answer. */
  postCount: number;
  /** True when one of them is a video (TikTok shows Duet and Stitch only for a video). */
  hasVideo: boolean;
}

export interface CampaignLaunchResponse {
  /** True: nothing changed; the lists say what a real call does. */
  dryRun: boolean;
  /** The drafts that this call scheduled (dry run: that it can schedule). */
  scheduled: string[];
  /** The posts that were scheduled before this call (a retry). They take no second unit. */
  alreadyScheduled: string[];
  /** The drafts that stay drafts, each with its reason. */
  needsYou: CampaignLaunchBlockedPost[];
  /** The questions that the request did not answer. Empty: the launch needs no question. */
  questions: CampaignLaunchQuestion[];
}

/** The stored default of a YouTube channel: who can see its next videos. Nothing else is stored. */
export interface YouTubePostingDefault {
  privacyStatus: YouTubePrivacyStatus;
}

/**
 * The stored posting defaults of one channel. Only YouTube has an entry, and it holds only the
 * privacy status: it selects the value of the next question and of the Composer. It never
 * completes the settings of a post, because "Made for kids" is asked for each campaign and each
 * post. TikTok forbids a stored or selected value.
 */
export interface ChannelPostingDefaults {
  youtube?: YouTubePostingDefault;
}

/** The platforms whose answer can be stored as the default of the channel. */
export const PLATFORMS_WITH_POSTING_DEFAULTS: readonly SettingsPlatform[] = ['youtube'];

export function platformStoresPostingDefaults(platform: string): boolean {
  return (PLATFORMS_WITH_POSTING_DEFAULTS as readonly string[]).includes(platform);
}

/**
 * Reads the stored column of a channel back as defaults, or null when it holds no valid value for
 * this platform. Never trusts the shape of the column. A `madeForKids` value of a row from before
 * 2026-10-11 is dropped.
 */
export function parseChannelPostingDefaults(platform: string, value: unknown): ChannelPostingDefaults | null {
  if (platform !== 'youtube' || value === null || typeof value !== 'object') {
    return null;
  }
  const { youtube } = value as Record<string, unknown>;
  if (youtube === null || youtube === undefined || typeof youtube !== 'object') {
    return null;
  }
  const { privacyStatus } = youtube as Record<string, unknown>;
  return isYouTubePrivacyStatus(privacyStatus) ? { youtube: { privacyStatus } } : null;
}

/** The number of answers the dialog asks: "Before we schedule: 2 quick answers". */
export function campaignQuestionCount(questions: readonly CampaignLaunchQuestion[]): number {
  return questions.reduce((sum, question) => sum + (question.platform === 'youtube' ? 2 : 1), 0);
}
